import React, { useCallback, useEffect, useRef, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { CheckCircle2Icon, XIcon } from 'lucide-react';
import { notifications, PurchaseNotification } from '../data/content';
import { useIsMobile } from '../hooks/useIsMobile';
const CONFIG = {
  desktop: {
    initialDelay: 8000,
    visible: 4000,
    interval: 6000
  },
  mobile: {
    initialDelay: 10000,
    visible: 3500,
    interval: 8000
  }
};
function pickNext(current: number): number {
  if (notifications.length <= 1) return 0;
  let next = current;
  while (next === current) {
    next = Math.floor(Math.random() * notifications.length);
  }
  return next;
}
export function SocialProofPopup() {
  const isMobile = useIsMobile();
  const [active, setActive] = useState<PurchaseNotification | null>(null);
  const [dismissed, setDismissed] = useState(false);
  const lastIndex = useRef(-1);
  const timers = useRef<number[]>([]);
  const config = isMobile ? CONFIG.mobile : CONFIG.desktop;
  const clearTimers = useCallback(() => {
    timers.current.forEach(timer => window.clearTimeout(timer));
    timers.current = [];
  }, []);
  useEffect(() => {
    if (dismissed || !notifications.length) {
      clearTimers();
      setActive(null);
      return;
    }
    const showNext = () => {
      const next = pickNext(lastIndex.current);
      lastIndex.current = next;
      setActive(notifications[next]);
      timers.current.push(window.setTimeout(() => {
        setActive(null);
        timers.current.push(window.setTimeout(showNext, config.interval));
      }, config.visible));
    };
    timers.current.push(window.setTimeout(showNext, config.initialDelay));
    return clearTimers;
  }, [clearTimers, config.initialDelay, config.interval, config.visible, dismissed]);
  return <div className="pointer-events-none fixed bottom-4 left-3 z-50 sm:bottom-5 sm:left-5" data-id="element-154" data-mp="1f16kd85oij4u">
      <AnimatePresence data-id="element-155" data-mp="1a2d6hemhcq45">
        {active && !dismissed && <motion.div key={`${active.name}-${active.time}`} initial={{
        x: '-120%',
        opacity: 0
      }} animate={{
        x: 0,
        opacity: 1
      }} exit={{
        opacity: 0
      }} transition={{
        duration: 0.25,
        ease: [0.23, 1, 0.32, 1]
      }} role="status" aria-live="polite" className="pointer-events-auto relative w-[220px] max-w-[58vw] rounded-lg border-l-[3px] border-gold bg-navy-mid px-3.5 py-2.5 shadow-[0_8px_24px_-8px_rgba(0,0,0,0.5)] sm:w-[280px] sm:max-w-none" data-id="element-156" data-mp="1h4iem14g6nvu">
            <button type="button" onClick={() => setDismissed(true)} aria-label="Close notification" className="absolute right-1.5 top-1.5 flex h-5 w-5 items-center justify-center rounded-full text-white/40 transition-colors duration-150 ease-out hover:text-white" data-id="element-157" data-mp="24srsmgvf67cf">
              <XIcon className="h-3.5 w-3.5" aria-hidden="true" data-id="element-158" data-mp="fhhkjtf1z8oh" />
            </button>
            <div className="flex items-center gap-1.5 pr-4" data-id="element-159" data-mp="kehqm4bwkbfl">
              <CheckCircle2Icon className="h-3.5 w-3.5 shrink-0 text-gold" aria-hidden="true" data-id="element-160" data-mp="cvs0bv4s9765" />
              <span className="text-[11px] font-bold text-white" data-id="element-161" data-mp="83qd5e62tw1c">Verified purchase</span>
            </div>
            <p className="mt-1 text-[11px] leading-snug text-white/80" data-id="element-162" data-mp="35ynkqswmm0b">
              {active.name} — {active.location}
            </p>
            <p className="mt-0.5 text-[10px] font-medium text-gold" data-id="element-163" data-mp="3u93u2s6insoj">{active.time}</p>
            <p className="mt-0.5 text-[10px] text-white/55" data-id="element-164" data-mp="vt2oajkyse6a">The ESL Survival Vault™</p>
          </motion.div>}
      </AnimatePresence>
    </div>;
}